import { getQuestionTypeMeta } from '@/components/FormBuilder/question-type-meta'
import type { FormPreviewSnapshot } from '@/contracts/form-preview'
import type {
  QuestionGroupMode,
  QuestionGroupTemplateContract,
  QuestionOutlineItem,
} from '@/contracts/question-groups'
import type { QuestionGroupUiContract } from '@/contracts/questionnaire'
import { publishForm, saveFormDetailSnapshot } from '@/lib/data-client/forms'
import { normalizeFormPreviewSnapshot } from '@/lib/form-preview/normalize-preview-snapshot'
import {
  addSyncedGroupToForm as addSyncedGroupToPersistentForm,
  copyGroupToForm as copyGroupToPersistentForm,
  copyQuestionToGroup as copyQuestionToPersistentGroup,
} from '@/lib/mock-api/question-groups'
import { samplePublicForm } from '@/lib/mock-data'
import {
  TAIWAN_LANDLINE_EXTENSION_PRESET,
  TAIWAN_LANDLINE_EXTENSION_REGEX,
  TAIWAN_MOBILE_PRESET,
  TAIWAN_MOBILE_REGEX,
} from '@/lib/phone-validation'
import { TAIWAN_ADDRESS_MODE, TAIWAN_ADDRESS_POSTAL_CODE_PRECISION } from '@/lib/taiwan-address'
import { isLayoutQuestionType } from '@/types/form'
import type {
  Choice,
  Form,
  LogicJump,
  Question,
  QuestionSettings,
  QuestionType,
} from '@/types/form'
import { create } from 'zustand'

export type BuilderSnapshot = {
  form: Form
  questions: Question[]
  logicJumps: LogicJump[]
  questionGroups: QuestionGroupUiContract[]
  questionOutline: QuestionOutlineItem[]
}

export type AppearancePreviewMode = 'theme' | 'font' | 'layout' | 'brand'

interface FormBuilderStore {
  form: Form
  questions: Question[]
  logicJumps: LogicJump[]
  questionGroups: QuestionGroupUiContract[]
  questionOutline: QuestionOutlineItem[]
  selectedQuestionId: string | null
  activeTab: 'question' | 'appearance' | 'logic' | 'binding'
  appearancePreviewMode: AppearancePreviewMode
  isDirty: boolean
  history: BuilderSnapshot[]
  historyIndex: number
  load: (
    form: Form,
    questions: Question[],
    logicJumps?: LogicJump[],
    questionGroups?: QuestionGroupUiContract[],
    questionOutline?: QuestionOutlineItem[],
  ) => void
  selectQuestion: (id: string | null) => void
  setActiveTab: (tab: 'question' | 'appearance' | 'logic' | 'binding') => void
  setAppearancePreviewMode: (mode: AppearancePreviewMode) => void
  addQuestion: (type: QuestionType, afterId?: string) => void
  updateQuestion: (id: string, patch: Partial<Question>) => void
  deleteQuestion: (id: string) => void
  reorderQuestions: (orderedIds: string[]) => void
  duplicateQuestion: (id: string) => void
  addQuestionGroup: () => void
  addSyncedQuestionGroup: (templateId: string) => void
  copyTemplateQuestionGroup: (templateId: string) => void
  addQuestionGroupFromTemplate: (
    template: QuestionGroupTemplateContract,
    mode: QuestionGroupMode,
  ) => void
  copyQuestionToGroupInstance: (
    questionId: string,
    groupInstanceId: string,
  ) => { ok: boolean; message: string }
  updateQuestionGroup: (id: string, patch: Partial<QuestionGroupUiContract>) => void
  deleteQuestionGroup: (id: string) => void
  reorderQuestionGroups: (orderedIds: string[]) => void
  reorderQuestionOutline: (orderedItemIds: string[]) => void
  reorderGroupQuestions: (groupId: string, orderedQuestionIds: string[]) => void
  moveQuestionToGroup: (questionId: string, groupId: string | null) => void
  addLogicJump: (jump: LogicJump) => void
  updateLogicJump: (id: string, patch: Partial<LogicJump>) => void
  deleteLogicJump: (id: string) => void
  reorderLogicJumps: (questionId: string, orderedIds: string[]) => void
  updateForm: (patch: Partial<Form>) => void
  undo: () => void
  redo: () => void
  markSaved: () => void
  save: () => Promise<void>
  publish: () => Promise<void>
}

function snapshot(
  form: Form,
  questions: Question[],
  logicJumps: LogicJump[] = [],
  questionGroups: QuestionGroupUiContract[] = [],
  questionOutline: QuestionOutlineItem[] = [],
): BuilderSnapshot {
  return {
    form: structuredClone(form),
    questions: structuredClone(questions),
    logicJumps: structuredClone(logicJumps),
    questionGroups: structuredClone(questionGroups),
    questionOutline: structuredClone(questionOutline),
  }
}

export function createBuilderDraftPreviewSnapshot(
  builderSnapshot: BuilderSnapshot,
): FormPreviewSnapshot {
  return normalizeFormPreviewSnapshot({
    source: 'builder_draft',
    mode: 'full_flow',
    form: builderSnapshot.form,
    questions: builderSnapshot.questions,
    groups: builderSnapshot.questionGroups,
    outline: builderSnapshot.questionOutline,
    logicJumps: builderSnapshot.logicJumps,
    hiddenFields:
      'hiddenFields' in builderSnapshot.form
        ? (builderSnapshot.form.hiddenFields as Record<string, string>)
        : {},
  })
}

function outlineItemId(item: QuestionOutlineItem) {
  return item.kind === 'question' ? `question:${item.questionId}` : `group:${item.groupInstanceId}`
}

function createQuestionOutline(
  questions: Pick<Question, 'id' | 'groupId' | 'orderIndex'>[],
  groups: Pick<QuestionGroupUiContract, 'id' | 'orderIndex' | 'questionIds'>[],
): QuestionOutlineItem[] {
  const sortedGroups = [...groups].sort((left, right) => left.orderIndex - right.orderIndex)
  const groupedQuestionIds = new Set(sortedGroups.flatMap((group) => group.questionIds))
  const ungroupedQuestions = questions
    .filter((question) => !question.groupId && !groupedQuestionIds.has(question.id))
    .sort((left, right) => left.orderIndex - right.orderIndex)

  return [
    ...ungroupedQuestions.map((question) => ({
      kind: 'question' as const,
      questionId: question.id,
      groupInstanceId: null,
    })),
    ...sortedGroups.map((group) => ({
      kind: 'group' as const,
      groupInstanceId: group.id,
    })),
  ]
}

function normalizeQuestionGroups(
  formId: string,
  groups: QuestionGroupUiContract[] = [],
  questions: Question[] = [],
) {
  const knownQuestionIds = new Set(questions.map((question) => question.id))
  return groups.map((group, orderIndex) => ({
    ...group,
    formId,
    orderIndex: group.orderIndex ?? orderIndex,
    collapsed: group.collapsed ?? false,
    mode: group.mode ?? 'copy',
    questionIds: group.questionIds.filter(
      (questionId) => knownQuestionIds.has(questionId) || group.mode === 'synced',
    ),
  }))
}

function normalizeBuilderDetail(params: {
  form: Form
  questions: Question[]
  logicJumps?: LogicJump[]
  questionGroups?: QuestionGroupUiContract[]
  questionOutline?: QuestionOutlineItem[]
}) {
  const groups = normalizeQuestionGroups(
    params.form.id,
    params.questionGroups ?? [],
    params.questions,
  )
  const groupIdByQuestionId = new Map<string, string>()
  for (const group of groups) {
    if (group.mode === 'synced') continue
    for (const questionId of group.questionIds) groupIdByQuestionId.set(questionId, group.id)
  }

  const questions = params.questions.map((question, orderIndex) => ({
    ...question,
    formId: params.form.id,
    groupId: groupIdByQuestionId.get(question.id),
    orderIndex,
  }))
  const validQuestionIds = new Set(questions.map((question) => question.id))
  const validGroupIds = new Set(groups.map((group) => group.id))
  const usedOutlineIds = new Set<string>()
  const normalizedOutline = (params.questionOutline ?? [])
    .filter((item) => {
      const valid =
        item.kind === 'question'
          ? validQuestionIds.has(item.questionId)
          : validGroupIds.has(item.groupInstanceId)
      if (!valid) return false
      const key = outlineItemId(item)
      if (usedOutlineIds.has(key)) return false
      usedOutlineIds.add(key)
      return true
    })
    .map((item) =>
      item.kind === 'question'
        ? { kind: 'question' as const, questionId: item.questionId, groupInstanceId: null }
        : { kind: 'group' as const, groupInstanceId: item.groupInstanceId },
    )

  const fallbackOutline = createQuestionOutline(questions, groups)
  const outline =
    normalizedOutline.length > 0
      ? [
          ...normalizedOutline,
          ...fallbackOutline.filter((item) => !usedOutlineIds.has(outlineItemId(item))),
        ]
      : fallbackOutline

  return {
    questions,
    logicJumps: params.logicJumps ?? [],
    questionGroups: groups,
    questionOutline: outline,
  }
}

function syncQuestionGroupsWithQuestions(groups: QuestionGroupUiContract[], questions: Question[]) {
  const questionIds = new Set(questions.map((question) => question.id))
  return groups.map((group, orderIndex) => ({
    ...group,
    orderIndex,
    questionIds:
      group.mode === 'synced'
        ? group.questionIds
        : group.questionIds.filter((id) => questionIds.has(id)),
  }))
}

function cloneTemplateQuestionsForForm(
  template: QuestionGroupTemplateContract,
  formId: string,
  groupInstanceId: string,
  startOrderIndex: number,
) {
  const questionIdMap = new Map<string, string>()
  const questions = template.questions.map((question, index) => {
    const nextQuestionId = crypto.randomUUID()
    questionIdMap.set(question.id, nextQuestionId)
    return {
      ...structuredClone(question),
      id: nextQuestionId,
      formId,
      groupId: groupInstanceId,
      orderIndex: startOrderIndex + index,
      choices: question.choices.map((choice, choiceIndex) => ({
        ...choice,
        id: crypto.randomUUID(),
        questionId: nextQuestionId,
        orderIndex: choiceIndex,
      })),
    }
  })

  const logicJumps = template.logicJumps.map((jump, order) => ({
    ...structuredClone(jump),
    id: crypto.randomUUID(),
    questionId: questionIdMap.get(jump.questionId) ?? jump.questionId,
    targetQuestionId: jump.targetQuestionId
      ? (questionIdMap.get(jump.targetQuestionId) ?? jump.targetQuestionId)
      : null,
    targetGroupId: jump.targetGroupId ? groupInstanceId : null,
    order,
    conditions: jump.conditions.map((condition) => ({
      ...condition,
      field: questionIdMap.get(condition.field) ?? condition.field,
    })),
  }))

  return { questions, logicJumps }
}

function createQuestionGroupInstanceFromTemplate(
  template: QuestionGroupTemplateContract,
  formId: string,
  mode: QuestionGroupMode,
  orderIndex: number,
  currentQuestions: Question[],
) {
  const instanceId = crypto.randomUUID()
  const startOrderIndex =
    currentQuestions.reduce((max, question) => Math.max(max, question.orderIndex), -1) + 1
  const cloned =
    mode === 'copy'
      ? cloneTemplateQuestionsForForm(template, formId, instanceId, startOrderIndex)
      : null
  const questions = cloned?.questions ?? template.questions
  const logicJumps = cloned?.logicJumps ?? template.logicJumps

  return {
    instance: {
      id: instanceId,
      formId,
      sourceGroupId: template.id,
      mode,
      title: mode === 'copy' ? `${template.title}（複本）` : template.title,
      description: template.description,
      orderIndex,
      collapsed: false,
      questionIds: questions.map((question) => question.id),
      questions,
      logicJumps,
      lockedReason:
        mode === 'synced' ? '同步題組需回到題組管理編輯，問卷內只可排序與設定跳轉。' : undefined,
      sourceMeta: template.sourceMeta,
    } satisfies QuestionGroupUiContract,
    questions: cloned?.questions ?? [],
    logicJumps: cloned?.logicJumps ?? [],
  }
}

function getGroupByQuestionId(groups: QuestionGroupUiContract[], questionId: string) {
  return groups.find((group) => group.questionIds.includes(questionId))
}

function isSyncedGroupQuestion(groups: QuestionGroupUiContract[], questionId: string) {
  return getGroupByQuestionId(groups, questionId)?.mode === 'synced'
}

function getDefaultQuestionTargetGroup(
  groups: QuestionGroupUiContract[],
  questions: Question[],
  afterId?: string,
  selectedQuestionId?: string | null,
) {
  const afterGroup = afterId ? getGroupByQuestionId(groups, afterId) : undefined
  if (afterGroup?.mode === 'copy') return afterGroup.id
  const selectedGroup = selectedQuestionId
    ? getGroupByQuestionId(groups, selectedQuestionId)
    : undefined
  if (selectedGroup?.mode === 'copy') return selectedGroup.id
  const questionGroupId = questions.find((question) => question.id === afterId)?.groupId
  if (
    questionGroupId &&
    groups.some((group) => group.id === questionGroupId && group.mode === 'copy')
  ) {
    return questionGroupId
  }
  return undefined
}

function questionTitle(type: QuestionType) {
  const meta = getQuestionTypeMeta(type)
  return meta.answerable ? `${meta.zhName}問題` : meta.zhName
}

function createDefaultChoices(type: QuestionType, questionId: string): Choice[] {
  if (['MULTIPLE_CHOICE', 'CHECKBOXES', 'DROPDOWN', 'PICTURE_CHOICE', 'RANKING'].includes(type)) {
    return ['選項 1', '選項 2'].map((label, orderIndex) => ({
      id: crypto.randomUUID(),
      questionId,
      label,
      value: `option-${orderIndex + 1}`,
      orderIndex,
    }))
  }

  if (type === 'NESTED_SELECT') {
    return ['北區', '中區', '南區'].map((label, orderIndex) => ({
      id: crypto.randomUUID(),
      questionId,
      label,
      value: `region-${orderIndex + 1}`,
      orderIndex,
    }))
  }

  return []
}

function createDefaultSettings(type: QuestionType): QuestionSettings {
  const common: QuestionSettings = { showQuestionNumber: true }

  if (['MULTIPLE_CHOICE', 'CHECKBOXES', 'DROPDOWN', 'PICTURE_CHOICE', 'RANKING'].includes(type)) {
    return {
      ...common,
      choiceValueMode: 'auto',
      allowMultiple: type === 'CHECKBOXES',
      showOther: false,
      randomize: false,
      autoAdvance: type === 'MULTIPLE_CHOICE',
      enableChoiceScoring: false,
      defaultChoiceScore: 0,
      defaultChoiceAmount: 0,
    }
  }

  if (type === 'EMAIL') {
    return {
      ...common,
      placeholder: 'name@example.com',
      validation: 'email',
      validationPreset: 'email',
      validationRegex: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
      validationTestValue: 'buyer@example.com',
    }
  }

  if (type === 'WEBSITE') {
    return {
      ...common,
      placeholder: 'https://example.com',
      validation: 'url',
      validationPreset: 'url',
      validationRegex: '^https?://.+\\..+',
      validationTestValue: 'https://miao-form.case4.app',
    }
  }

  if (type === 'TAIWAN_MOBILE') {
    return {
      ...common,
      placeholder: '0912345678',
      validation: 'phone',
      validationPreset: TAIWAN_MOBILE_PRESET,
      validationRegex: TAIWAN_MOBILE_REGEX,
      validationTestValue: '0912345678',
    }
  }

  if (type === 'TAIWAN_LANDLINE') {
    return {
      ...common,
      placeholder: '2345-6789',
      validation: 'phone',
      validationPreset: TAIWAN_LANDLINE_EXTENSION_PRESET,
      validationRegex: TAIWAN_LANDLINE_EXTENSION_REGEX,
      validationTestValue: '02-2345-6789#123',
      taiwanLandlineAreaCode: '02',
    }
  }

  if (type === 'TAIWAN_ADDRESS') {
    return {
      ...common,
      addressMode: TAIWAN_ADDRESS_MODE,
      postalCodePrecision: TAIWAN_ADDRESS_POSTAL_CODE_PRECISION,
      detailPlaceholder: '請輸入路名、段、巷弄、門牌、樓層',
    }
  }

  if (type === 'TIME') {
    return {
      ...common,
      placeholder: '09:30',
    }
  }

  if (type === 'NUMBER') {
    return {
      ...common,
      placeholder: '請輸入數字',
      numberMin: 0,
      numberMax: 1000000,
      numberStep: 1,
      numberUnit: '',
      numberDecimals: 0,
      numberUseThousands: true,
      numberPrefix: '',
      numberSuffix: '',
      validation: 'number',
      validationPreset: 'number',
      validationRegex: '^-?\\d+(\\.\\d+)?$',
      validationTestValue: '1200',
    }
  }

  if (type === 'NUMBER_SLIDER') {
    return {
      ...common,
      sliderMin: 0,
      sliderMax: 100,
      sliderStep: 1,
      sliderShowValue: true,
      sliderShowTicks: true,
      sliderTickLabels: ['低', '中', '高'],
      startLabel: '低',
      endLabel: '高',
    }
  }

  if (type === 'TOTAL') {
    return {
      ...common,
      totalMode: 'sum',
      totalPreviewValue: 120000,
      totalUnit: '元',
      totalPrefix: '預估合計',
      totalSuffix: '未稅',
      totalFormula: '',
      buttonText: '確認總計',
    }
  }

  if (type === 'MATRIX') {
    return {
      ...common,
      selectionMode: 'single',
      rows: ['品質', '價格', '服務'],
      columns: ['不同意', '普通', '同意'],
    }
  }

  if (type === 'NESTED_SELECT') {
    return {
      ...common,
      placeholder: '請依序選擇',
      nestedLevels: ['區域', '縣市', '負責據點'],
      nestedOptions: [
        {
          id: crypto.randomUUID(),
          label: '北區',
          value: 'north',
          children: [
            {
              id: crypto.randomUUID(),
              label: '台北市',
              value: 'taipei',
              children: [
                { id: crypto.randomUUID(), label: '台北一區據點', value: 'taipei-branch-1' },
                { id: crypto.randomUUID(), label: '台北二區據點', value: 'taipei-branch-2' },
              ],
            },
            {
              id: crypto.randomUUID(),
              label: '新北市',
              value: 'new-taipei',
              children: [
                { id: crypto.randomUUID(), label: '新北板橋據點', value: 'banqiao-branch' },
                { id: crypto.randomUUID(), label: '新北新店據點', value: 'xindian-branch' },
              ],
            },
          ],
        },
        {
          id: crypto.randomUUID(),
          label: '中區',
          value: 'central',
          children: [
            {
              id: crypto.randomUUID(),
              label: '台中市',
              value: 'taichung',
              children: [
                { id: crypto.randomUUID(), label: '台中西屯據點', value: 'xitun-branch' },
                { id: crypto.randomUUID(), label: '台中北屯據點', value: 'beitun-branch' },
              ],
            },
            {
              id: crypto.randomUUID(),
              label: '彰化縣',
              value: 'changhua',
              children: [
                { id: crypto.randomUUID(), label: '彰化市據點', value: 'changhua-city-branch' },
              ],
            },
          ],
        },
      ],
    }
  }

  if (type === 'SECTION_TITLE') {
    return {
      ...common,
      showQuestionNumber: false,
      layoutTone: 'accent',
      layoutAlignment: 'left',
      buttonText: '進入下一段',
    }
  }

  if (type === 'INTRO_BLOCK' || type === 'STATEMENT') {
    return {
      ...common,
      showQuestionNumber: false,
      layoutTone: 'neutral',
      layoutAlignment: 'left',
      calloutLabel: '填答說明',
      buttonText: '我知道了',
    }
  }

  if (type === 'DIVIDER') {
    return {
      ...common,
      showQuestionNumber: false,
      dividerStyle: 'solid',
      dividerLabel: '下一段',
      layoutAlignment: 'center',
      buttonText: '繼續',
    }
  }

  return common
}

export const useFormBuilderStore = create<FormBuilderStore>((set, get) => {
  const initialGroups = normalizeBuilderDetail({
    form: samplePublicForm,
    questions: samplePublicForm.questions,
    logicJumps: samplePublicForm.logicJumps,
    questionGroups: [],
    questionOutline: [],
  })
  const initial = snapshot(
    samplePublicForm,
    initialGroups.questions,
    samplePublicForm.logicJumps,
    initialGroups.questionGroups,
    initialGroups.questionOutline,
  )

  function commit(
    next: Omit<BuilderSnapshot, 'questionOutline'> & { questionOutline?: QuestionOutlineItem[] },
    selectedQuestionId?: string | null,
  ) {
    const normalized = normalizeBuilderDetail({
      form: next.form,
      questions: next.questions,
      logicJumps: next.logicJumps,
      questionGroups: next.questionGroups,
      questionOutline: next.questionOutline ?? get().questionOutline,
    })
    const resolvedNext: BuilderSnapshot = {
      form: next.form,
      questions: normalized.questions,
      logicJumps: normalized.logicJumps,
      questionGroups: normalized.questionGroups,
      questionOutline: normalized.questionOutline,
    }
    const history = get().history.slice(0, get().historyIndex + 1)
    history.push(
      snapshot(
        resolvedNext.form,
        resolvedNext.questions,
        resolvedNext.logicJumps,
        resolvedNext.questionGroups,
        resolvedNext.questionOutline,
      ),
    )
    set({
      ...resolvedNext,
      selectedQuestionId: selectedQuestionId ?? get().selectedQuestionId,
      isDirty: true,
      history,
      historyIndex: history.length - 1,
    })
  }

  return {
    form: initial.form,
    questions: initial.questions,
    logicJumps: initial.logicJumps,
    questionGroups: initial.questionGroups,
    questionOutline: initial.questionOutline,
    selectedQuestionId: initial.questions[0]?.id ?? null,
    activeTab: 'question',
    appearancePreviewMode: 'theme',
    isDirty: false,
    history: [initial],
    historyIndex: 0,
    load: (form, questions, logicJumps = [], questionGroups = [], questionOutline = []) => {
      const normalizedGroups = normalizeBuilderDetail({
        form,
        questions,
        logicJumps,
        questionGroups,
        questionOutline,
      })
      const next = snapshot(
        form,
        normalizedGroups.questions,
        normalizedGroups.logicJumps,
        normalizedGroups.questionGroups,
        normalizedGroups.questionOutline,
      )
      set({
        form: next.form,
        questions: next.questions,
        logicJumps: next.logicJumps,
        questionGroups: next.questionGroups,
        questionOutline: next.questionOutline,
        selectedQuestionId: next.questions[0]?.id ?? null,
        activeTab: 'question',
        appearancePreviewMode: 'theme',
        isDirty: false,
        history: [next],
        historyIndex: 0,
      })
    },
    selectQuestion: (id) => set({ selectedQuestionId: id }),
    setActiveTab: (activeTab) => set({ activeTab }),
    setAppearancePreviewMode: (appearancePreviewMode) => set({ appearancePreviewMode }),
    addQuestion: (type, afterId) => {
      const state = get()
      const index = afterId
        ? state.questions.findIndex((question) => question.id === afterId) + 1
        : state.questions.length
      const sourceGroupId = getDefaultQuestionTargetGroup(
        state.questionGroups,
        state.questions,
        afterId,
        state.selectedQuestionId,
      )
      const questionId = crypto.randomUUID()
      const question: Question = {
        id: questionId,
        formId: state.form.id,
        groupId: sourceGroupId,
        type,
        title: questionTitle(type),
        description: '',
        required: !isLayoutQuestionType(type),
        settings: createDefaultSettings(type),
        choices: createDefaultChoices(type, questionId),
        orderIndex: index,
      }
      const questions = [...state.questions]
      questions.splice(index, 0, question)
      const questionGroups = state.questionGroups.map((group) =>
        group.id === sourceGroupId
          ? { ...group, questionIds: [...group.questionIds, question.id] }
          : group,
      )
      const questionOutline =
        sourceGroupId || afterId
          ? state.questionOutline
          : [
              ...state.questionOutline,
              { kind: 'question' as const, questionId: question.id, groupInstanceId: null },
            ]
      commit(
        {
          form: state.form,
          questions: questions.map((item, orderIndex) => ({ ...item, orderIndex })),
          logicJumps: state.logicJumps,
          questionGroups,
          questionOutline,
        },
        question.id,
      )
    },
    updateQuestion: (id, patch) => {
      const state = get()
      if (isSyncedGroupQuestion(state.questionGroups, id)) return
      commit({
        form: state.form,
        questions: state.questions.map((question) =>
          question.id === id
            ? { ...question, ...patch, settings: patch.settings ?? question.settings }
            : question,
        ),
        logicJumps: state.logicJumps,
        questionGroups: state.questionGroups,
        questionOutline: state.questionOutline,
      })
    },
    deleteQuestion: (id) => {
      const state = get()
      if (isSyncedGroupQuestion(state.questionGroups, id)) return
      const questions = state.questions.filter((question) => question.id !== id)
      const remainingQuestionIds = new Set(questions.map((question) => question.id))
      const logicJumps = state.logicJumps
        .filter(
          (jump) =>
            jump.questionId !== id &&
            (jump.targetQuestionId === null || remainingQuestionIds.has(jump.targetQuestionId)),
        )
        .map((jump) => ({
          ...jump,
          conditions: jump.conditions.filter((condition) => condition.field !== id),
          displayTargets: jump.displayTargets?.filter((target) => {
            if (target.type === 'group') return true
            return target.questionId !== id && remainingQuestionIds.has(target.questionId)
          }),
        }))
        .filter((jump) => jump.conditions.length > 0)
      commit(
        {
          form: state.form,
          questions: questions.map((question, orderIndex) => ({ ...question, orderIndex })),
          logicJumps,
          questionGroups: syncQuestionGroupsWithQuestions(state.questionGroups, questions),
          questionOutline: state.questionOutline.filter(
            (item) => item.kind !== 'question' || item.questionId !== id,
          ),
        },
        questions[0]?.id ?? null,
      )
    },
    reorderQuestions: (orderedIds) => {
      const state = get()
      const byId = new Map(state.questions.map((question) => [question.id, question]))
      commit({
        form: state.form,
        questions: orderedIds
          .map((id, orderIndex) => {
            const question = byId.get(id)
            return question ? { ...question, orderIndex } : null
          })
          .filter(Boolean) as Question[],
        logicJumps: state.logicJumps,
        questionGroups: state.questionGroups.map((group) => ({
          ...group,
          questionIds: orderedIds.filter((id) => group.questionIds.includes(id)),
        })),
        questionOutline: state.questionOutline,
      })
    },
    duplicateQuestion: (id) => {
      const state = get()
      if (isSyncedGroupQuestion(state.questionGroups, id)) return
      const source = state.questions.find((question) => question.id === id)
      if (!source) return
      const copy = {
        ...structuredClone(source),
        id: crypto.randomUUID(),
        title: `${source.title}（副本）`,
        choices: source.choices.map((choice) => ({ ...choice, id: crypto.randomUUID() })),
      }
      const index = state.questions.findIndex((question) => question.id === id) + 1
      const questions = [...state.questions]
      questions.splice(index, 0, copy)
      const questionGroups = state.questionGroups.map((group) =>
        group.id === copy.groupId
          ? { ...group, questionIds: [...group.questionIds, copy.id] }
          : group,
      )
      const normalizedCopyQuestions = questions.map((question, orderIndex) => ({
        ...question,
        orderIndex,
        choices:
          question.id === copy.id
            ? question.choices.map((choice) => ({ ...choice, questionId: copy.id }))
            : question.choices,
      }))
      commit(
        {
          form: state.form,
          questions: normalizedCopyQuestions,
          logicJumps: state.logicJumps,
          questionGroups,
          questionOutline: copy.groupId
            ? state.questionOutline
            : state.questionOutline.flatMap((item) =>
                item.kind === 'question' && item.questionId === id
                  ? [
                      item,
                      {
                        kind: 'question' as const,
                        questionId: copy.id,
                        groupInstanceId: null,
                      },
                    ]
                  : [item],
              ),
        },
        copy.id,
      )
    },
    addQuestionGroup: () => {
      const state = get()
      const id = crypto.randomUUID()
      const questionGroup: QuestionGroupUiContract = {
        id,
        formId: state.form.id,
        sourceGroupId: id,
        mode: 'copy',
        title: `自訂題組 ${state.questionGroups.length + 1}`,
        description: '只使用於目前問卷的複本題組。',
        orderIndex: state.questionGroups.length,
        collapsed: false,
        questionIds: [],
      }
      commit({
        form: state.form,
        questions: state.questions,
        logicJumps: state.logicJumps,
        questionGroups: [...state.questionGroups, questionGroup],
        questionOutline: [
          ...state.questionOutline,
          { kind: 'group', groupInstanceId: questionGroup.id },
        ],
      })
    },
    addSyncedQuestionGroup: (templateId) => {
      const state = get()
      const instance = addSyncedGroupToPersistentForm(state.form.id, templateId)
      if (!instance) return
      commit({
        form: state.form,
        questions: state.questions,
        logicJumps: state.logicJumps,
        questionGroups: [...state.questionGroups, instance],
        questionOutline: [
          ...state.questionOutline,
          { kind: 'group', groupInstanceId: instance.id },
        ],
      })
    },
    copyTemplateQuestionGroup: (templateId) => {
      const state = get()
      const instance = copyGroupToPersistentForm(state.form.id, templateId)
      if (!instance) return
      const copiedQuestions = instance.questions ?? []
      commit(
        {
          form: state.form,
          questions: [...state.questions, ...copiedQuestions],
          logicJumps: [...state.logicJumps, ...(instance.logicJumps ?? [])],
          questionGroups: [...state.questionGroups, instance],
          questionOutline: [
            ...state.questionOutline,
            { kind: 'group', groupInstanceId: instance.id },
          ],
        },
        copiedQuestions[0]?.id ?? state.selectedQuestionId,
      )
    },
    addQuestionGroupFromTemplate: (template, mode) => {
      const state = get()
      const { instance, questions, logicJumps } = createQuestionGroupInstanceFromTemplate(
        template,
        state.form.id,
        mode,
        state.questionGroups.length,
        state.questions,
      )
      commit(
        {
          form: state.form,
          questions: [...state.questions, ...questions],
          logicJumps: [...state.logicJumps, ...logicJumps],
          questionGroups: [...state.questionGroups, instance],
          questionOutline: [
            ...state.questionOutline,
            { kind: 'group', groupInstanceId: instance.id },
          ],
        },
        questions[0]?.id ?? state.selectedQuestionId,
      )
    },
    copyQuestionToGroupInstance: (questionId, groupInstanceId) => {
      const state = get()
      const result = copyQuestionToPersistentGroup(state.form.id, questionId, groupInstanceId)
      if (!result.ok) {
        return { ok: false, message: result.message }
      }
      commit(
        {
          form: state.form,
          questions: [...state.questions, result.question],
          logicJumps: state.logicJumps,
          questionGroups: state.questionGroups.map((group) =>
            group.id === result.group.id ? result.group : group,
          ),
          questionOutline: state.questionOutline,
        },
        result.question.id,
      )
      return { ok: true, message: `已建立複本到「${result.group.title}」。` }
    },
    updateQuestionGroup: (id, patch) => {
      const state = get()
      const group = state.questionGroups.find((item) => item.id === id)
      if (group?.mode === 'synced' && (patch.questions || patch.logicJumps || patch.questionIds)) {
        return
      }
      commit({
        form: state.form,
        questions: state.questions,
        logicJumps: state.logicJumps,
        questionGroups: state.questionGroups.map((group) =>
          group.id === id
            ? {
                ...group,
                ...patch,
                questionIds: patch.questionIds ?? group.questionIds,
              }
            : group,
        ),
        questionOutline: state.questionOutline,
      })
    },
    deleteQuestionGroup: (id) => {
      const state = get()
      const group = state.questionGroups.find((item) => item.id === id)
      const removeQuestions = group?.mode === 'copy'
      const removedQuestionIds = new Set(group?.questionIds ?? [])
      const questions = removeQuestions
        ? state.questions.filter((question) => !removedQuestionIds.has(question.id))
        : state.questions.map((question) =>
            question.groupId === id ? { ...question, groupId: undefined } : question,
          )
      commit({
        form: state.form,
        questions,
        logicJumps: state.logicJumps.filter(
          (jump) =>
            !removedQuestionIds.has(jump.questionId) &&
            !removedQuestionIds.has(jump.targetQuestionId ?? '') &&
            jump.targetGroupId !== id &&
            !jump.displayTargets?.some((target) => {
              if (target.type === 'group') return target.groupId === id
              return removedQuestionIds.has(target.questionId)
            }),
        ),
        questionGroups: state.questionGroups
          .filter((group) => group.id !== id)
          .map((group, orderIndex) => ({ ...group, orderIndex })),
        questionOutline: state.questionOutline.filter(
          (item) => item.kind !== 'group' || item.groupInstanceId !== id,
        ),
      })
    },
    reorderQuestionGroups: (orderedIds) => {
      const state = get()
      const byId = new Map(state.questionGroups.map((group) => [group.id, group]))
      commit({
        form: state.form,
        questions: state.questions,
        logicJumps: state.logicJumps,
        questionGroups: orderedIds
          .map((id, orderIndex) => {
            const group = byId.get(id)
            return group ? { ...group, orderIndex } : null
          })
          .filter(Boolean) as QuestionGroupUiContract[],
        questionOutline: state.questionOutline,
      })
    },
    reorderQuestionOutline: (orderedItemIds) => {
      const state = get()
      const byId = new Map(state.questionOutline.map((item) => [outlineItemId(item), item]))
      const ordered = orderedItemIds
        .map((id) => byId.get(id))
        .filter(Boolean) as QuestionOutlineItem[]
      const orderedIdSet = new Set(orderedItemIds)
      const untouched = state.questionOutline.filter(
        (item) => !orderedIdSet.has(outlineItemId(item)),
      )
      const nextOutline = [...ordered, ...untouched]
      const outlineOrderByQuestionId = new Map<string, number>()
      nextOutline.forEach((item, index) => {
        if (item.kind === 'question') outlineOrderByQuestionId.set(item.questionId, index)
      })
      commit({
        form: state.form,
        questions: state.questions.map((question) => ({
          ...question,
          orderIndex: outlineOrderByQuestionId.get(question.id) ?? question.orderIndex,
        })),
        logicJumps: state.logicJumps,
        questionGroups: state.questionGroups.map((group) => ({
          ...group,
          orderIndex: nextOutline.findIndex(
            (item) => item.kind === 'group' && item.groupInstanceId === group.id,
          ),
        })),
        questionOutline: nextOutline,
      })
    },
    reorderGroupQuestions: (groupId, orderedQuestionIds) => {
      const state = get()
      const group = state.questionGroups.find((item) => item.id === groupId)
      if (!group || group.mode === 'synced') return
      const orderedQuestionIdSet = new Set(orderedQuestionIds)
      const nextQuestionIds = [
        ...orderedQuestionIds.filter((questionId) => group.questionIds.includes(questionId)),
        ...group.questionIds.filter((questionId) => !orderedQuestionIdSet.has(questionId)),
      ]
      const groupOrderIndexById = new Map(
        nextQuestionIds.map((questionId, index) => [questionId, index]),
      )
      commit({
        form: state.form,
        questions: state.questions.map((question) =>
          question.groupId === groupId
            ? {
                ...question,
                orderIndex: groupOrderIndexById.get(question.id) ?? question.orderIndex,
              }
            : question,
        ),
        logicJumps: state.logicJumps,
        questionGroups: state.questionGroups.map((item) =>
          item.id === groupId ? { ...item, questionIds: nextQuestionIds } : item,
        ),
        questionOutline: state.questionOutline,
      })
    },
    moveQuestionToGroup: (questionId, groupId) => {
      const state = get()
      const targetGroupExists = groupId
        ? state.questionGroups.some((group) => group.id === groupId)
        : true
      if (!targetGroupExists) return
      if (isSyncedGroupQuestion(state.questionGroups, questionId)) return
      const targetGroup = groupId
        ? state.questionGroups.find((group) => group.id === groupId)
        : null
      if (targetGroup?.mode === 'synced') return

      commit({
        form: state.form,
        questions: state.questions.map((question) =>
          question.id === questionId ? { ...question, groupId: groupId ?? undefined } : question,
        ),
        logicJumps: state.logicJumps,
        questionGroups: state.questionGroups.map((group) => {
          const withoutQuestion = group.questionIds.filter((id) => id !== questionId)
          if (group.id !== groupId) return { ...group, questionIds: withoutQuestion }
          return { ...group, questionIds: [...withoutQuestion, questionId] }
        }),
        questionOutline:
          groupId === null
            ? [
                ...state.questionOutline,
                { kind: 'question' as const, questionId, groupInstanceId: null },
              ]
            : state.questionOutline.filter(
                (item) => item.kind !== 'question' || item.questionId !== questionId,
              ),
      })
    },
    addLogicJump: (jump) => {
      const state = get()
      const siblingJumps = state.logicJumps.filter((item) => item.questionId === jump.questionId)
      const nextJump = {
        ...jump,
        order: siblingJumps.some((item) => item.order === jump.order)
          ? siblingJumps.length
          : jump.order,
      }
      commit({
        form: state.form,
        questions: state.questions,
        logicJumps: [...state.logicJumps, nextJump],
        questionGroups: state.questionGroups,
        questionOutline: state.questionOutline,
      })
    },
    updateLogicJump: (id, patch) => {
      const state = get()
      commit({
        form: state.form,
        questions: state.questions,
        logicJumps: state.logicJumps.map((jump) =>
          jump.id === id
            ? { ...jump, ...patch, conditions: patch.conditions ?? jump.conditions }
            : jump,
        ),
        questionGroups: state.questionGroups,
        questionOutline: state.questionOutline,
      })
    },
    deleteLogicJump: (id) => {
      const state = get()
      const removed = state.logicJumps.find((jump) => jump.id === id)
      const logicJumps = state.logicJumps
        .filter((jump) => jump.id !== id)
        .map((jump) =>
          removed && jump.questionId === removed.questionId && jump.order > removed.order
            ? { ...jump, order: jump.order - 1 }
            : jump,
        )
      commit({
        form: state.form,
        questions: state.questions,
        logicJumps,
        questionGroups: state.questionGroups,
        questionOutline: state.questionOutline,
      })
    },
    reorderLogicJumps: (questionId, orderedIds) => {
      const state = get()
      const orderedIdSet = new Set(orderedIds)
      const byId = new Map(state.logicJumps.map((jump) => [jump.id, jump]))
      const reordered = orderedIds
        .map((id, order) => {
          const jump = byId.get(id)
          return jump ? { ...jump, order } : null
        })
        .filter(Boolean) as LogicJump[]
      const untouched = state.logicJumps.filter(
        (jump) => jump.questionId !== questionId || !orderedIdSet.has(jump.id),
      )
      commit({
        form: state.form,
        questions: state.questions,
        logicJumps: [...untouched, ...reordered],
        questionGroups: state.questionGroups,
        questionOutline: state.questionOutline,
      })
    },
    updateForm: (patch) => {
      const state = get()
      commit({
        form: { ...state.form, ...patch },
        questions: state.questions,
        logicJumps: state.logicJumps,
        questionGroups: state.questionGroups,
        questionOutline: state.questionOutline,
      })
    },
    undo: () => {
      const state = get()
      if (state.historyIndex <= 0) return
      const nextIndex = state.historyIndex - 1
      const next = state.history[nextIndex]
      set({ ...next, historyIndex: nextIndex, isDirty: true })
    },
    redo: () => {
      const state = get()
      if (state.historyIndex >= state.history.length - 1) return
      const nextIndex = state.historyIndex + 1
      const next = state.history[nextIndex]
      set({ ...next, historyIndex: nextIndex, isDirty: true })
    },
    markSaved: () => set({ isDirty: false }),
    save: async () => {
      await new Promise((resolve) => window.setTimeout(resolve, 250))
      const state = get()
      await saveFormDetailSnapshot({
        form: state.form,
        questions: state.questions,
        logicJumps: state.logicJumps,
        groups: state.questionGroups,
        outline: state.questionOutline,
      })
      set({ isDirty: false })
    },
    publish: async () => {
      const state = get()
      const nextForm = { ...state.form, status: 'published' as const }
      await saveFormDetailSnapshot({
        form: nextForm,
        questions: state.questions,
        logicJumps: state.logicJumps,
        groups: state.questionGroups,
        outline: state.questionOutline,
      })
      await publishForm(nextForm.id)
      set({ form: nextForm, isDirty: false })
    },
  }
})
